/**
 * PATH: packages/email-hub/src/feature/components/OrgMailboxHealthPanel.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-ORGMAILBOXHEALTH-001
 * PURPOSE: Admin-only org-wide mailbox health table — every mailbox in the
 *          organisation with its sync state, worst-first, so an admin can
 *          spot dead/stale mailboxes that belong to OTHER users.
 * WHY: Operator ask 2026-06-10: "there should be an admin dashboard where
 *      the admin gets these notifications … the users' mailbox should not
 *      be getting these notifications". The reconnect banner is now scoped
 *      to the caller's own mailboxes; this panel is the admin surface for
 *      everyone else's. Self-hides entirely for non-admins (the BE
 *      downgrades ?scope=org silently and reports can_view_org=false).
 * FLOW:
 *   ┌────────────────────────┐   ┌────────────────────┐   ┌──────────────┐
 *   │ /sync/status/?scope=org │ → │ sortMailboxesBy    │ → │ table rows + │
 *   │ (admin escape hatch)    │   │ Health (pure)      │   │ health pills │
 *   └────────────────────────┘   └────────────────────┘   └──────────────┘
 * DEPENDENCIES:
 *   - useEmails.fetchEmailSyncStatus: the scope=org fetch
 *   - lib/orgMailboxHealth: pure classify/sort/summarise selectors
 * RELATED:
 *   - pages/SyncSettingsPage.tsx: the mount point ("Sync & Status")
 *   - MailboxReconnectBanner.tsx: user-scope equivalent (own mailboxes)
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  PauseCircle,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import type {
  EmailMailboxSyncStatus,
  EmailSyncStatusResponse,
} from '../hooks/useEmails.types'
import { fetchEmailSyncStatus } from '../hooks/useEmails'
import {
  classifyMailboxHealth,
  sortMailboxesByHealth,
  summariseMailboxHealth,
  type MailboxHealth,
} from '../lib/orgMailboxHealth'

function formatAge(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return 'never'
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

const HEALTH_PILL: Record<MailboxHealth, { label: string; cls: string }> = {
  dead: {
    label: 'Reconnect required',
    cls: 'bg-red-500/15 text-red-600 dark:text-red-400',
  },
  error: {
    label: 'Sync error',
    cls: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  },
  stale: {
    label: 'Stale',
    cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-500',
  },
  paused: {
    label: 'Paused',
    cls: 'bg-muted text-muted-foreground',
  },
  ok: {
    label: 'Healthy',
    cls: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  },
}

function HealthPill({ health }: { health: MailboxHealth }) {
  const { label, cls } = HEALTH_PILL[health]
  return (
    <span className={cn('px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap', cls)}>
      {label}
    </span>
  )
}

function MailboxRow({ m }: { m: EmailMailboxSyncStatus }) {
  const health = classifyMailboxHealth(m)
  return (
    <li className="flex items-center gap-3 py-2 border-b border-border last:border-0 text-xs">
      <div className="flex-1 min-w-0">
        <p className="truncate font-medium" title={m.email_address}>
          {m.email_address}
        </p>
        <p className="text-[10px] text-muted-foreground">
          {m.provider === 'gmail' ? 'Gmail' : 'Microsoft'}
          {m.account_type === 'shared' ? ' · shared' : ''}
          {' · synced '}
          {formatAge(m.last_sync_age_seconds)}
        </p>
        {m.last_sync_error && (
          <p
            className="text-[10px] text-red-500/80 truncate"
            title={m.last_sync_error}
          >
            {m.last_sync_error}
          </p>
        )}
      </div>
      <HealthPill health={health} />
    </li>
  )
}

/**
 * Self-contained: fetches `?scope=org` on mount, renders nothing for
 * non-admins. The fetch is one-shot + manual refresh (no poll) — this is
 * a diagnostics surface, not a live monitor.
 */
export default function OrgMailboxHealthPanel({ className }: { className?: string }) {
  const [data, setData] = useState<EmailSyncStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await fetchEmailSyncStatus(undefined, { scope: 'org' }))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const mailboxes = useMemo(
    () => sortMailboxesByHealth(data?.mailboxes),
    [data?.mailboxes],
  )
  const counts = useMemo(
    () => summariseMailboxHealth(data?.mailboxes),
    [data?.mailboxes],
  )

  // Non-admin (BE said so), still loading the first time, or older BE
  // without the scope extension → render nothing. The panel must never
  // tease org data to users who can't have it.
  if (!data || data.can_view_org !== true) return null

  const troubled = counts.dead + counts.error

  return (
    <section
      className={cn('rounded-lg border border-border bg-card p-5 space-y-3', className)}
      data-testid="org-mailbox-health-panel"
    >
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-medium flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" /> Organisation mailbox health
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-normal">
              Admin
            </span>
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Every connected mailbox in your organisation - including other
            users&apos; - worst first. Dead mailboxes need their OWNER to
            reconnect from their own login.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { void load() }}
          disabled={loading}
          className="text-xs p-1.5 rounded-md border border-border hover:bg-accent transition-colors disabled:opacity-50"
          aria-label="Refresh mailbox health"
        >
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" />
          )}
        </button>
      </div>

      {troubled > 0 ? (
        <div className="text-xs flex items-center gap-1.5 px-3 py-2 rounded-md bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/30">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          {counts.dead > 0 && `${counts.dead} mailbox${counts.dead === 1 ? '' : 'es'} need reconnection`}
          {counts.dead > 0 && counts.error > 0 && ' · '}
          {counts.error > 0 && `${counts.error} with sync errors`}
        </div>
      ) : (
        <div className="text-xs flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          All {counts.ok} active mailbox{counts.ok === 1 ? '' : 'es'} healthy
          {counts.paused > 0 && (
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <PauseCircle className="w-3 h-3" /> {counts.paused} paused
            </span>
          )}
        </div>
      )}

      {mailboxes.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No mailboxes connected in this organisation yet.
        </p>
      ) : (
        <ul>
          {mailboxes.map((m) => <MailboxRow key={m.id} m={m} />)}
        </ul>
      )}
    </section>
  )
}
